// Data-source abstraction with dev-mode fixtures fallback
// (Phase 4.5 deliverable #7).
//
// The UI's screens read from a `DataSource` rather than from
// fixtures or the API client directly. The data source's
// implementation is selected at app start:
//
//   * Production: ``ApiDataSource`` — calls the backend over
//     HTTP via :class:`ApiClient`. Throws on backend down.
//   * Dev: ``FixtureDataSource`` — returns the static fixtures
//     so the UI builds standalone without the backend running.
//
// The toggle is environment-controlled via
// ``import.meta.env.VITE_USE_FIXTURES``. Default is the API
// data source (production posture); developers opt in to
// fixtures by setting `VITE_USE_FIXTURES=true` in their `.env`.
//
// Adversarial-review note: the `useFixtures()` helper exposes
// the toggle so the app shell can surface a visible banner
// when fixtures are active. This makes it impossible to ship
// a production build that silently uses fixtures.

import { ApiClient } from "./client";
import type {
  AccountWire,
  AiCostStatusWire,
  AutoApprovalsConfigWire,
  ApprovalReviewResponse,
  ApprovalRepriceRequestBody,
  ApprovalResponse,
  BackupTestsResponseWire,
  BackupVerifyResponseWire,
  BasketWire,
  CryptoWatchlistAnalysisRowWire,
  CryptoValidationRunWire,
  ClosedStockTradesResponseWire,
  ClosedStockTradeWire,
  ChartSnapshotWire,
  ConfirmAndPlaceResponse,
  DecisionReflectionWire,
  EvaluationResultWire,
  EventRowWire,
  HealthResponse,
  AIWorkflowStatusResponseWire,
  InvestmentPositionWire,
  LivePnLResponseWire,
  MoonshotAIRankResponseWire,
  MoonshotSignalsResponseWire,
  MoonshotStockSignalWire,
  MultiAgentReviewResultWire,
  PendingApprovalWire,
  PendingConfigChangeWire,
  PositionReassessmentListResponseWire,
  PositionReassessmentWire,
  PositionTradeWire,
  ResearchReportCreateResponseWire,
  PositionWire,
  ResearchReportWire,
  ResearchSuggestedSignalWire,
  RiskSnapshotResponse,
  RunAgentReviewResponseWire,
  ShadowAgentReviewRunResponseWire,
  SavedResearchEvaluationWire,
  SocialSentimentSnapshotWire,
  RiskLimitWire,
  SectorLimitsUpdateBody,
  SectorLimitsWire,
  SignalFailureWire,
  SignalHistoryWire,
  SignalWire,
  SignalsGenerateWireResponse,
  StrategyRouteWire,
  TradePreviewWire,
} from "./client";
import type {
  Account,
  Basket,
  ChartSnapshot,
  ChartTimeframe,
  ClosedStockTrade,
  DecisionReflection,
  EvaluationResult,
  EventRow,
  Investment,
  LimitCheckRow,
  LivePnLEntry,
  LivePnLSnapshot,
  MoonshotAIRankResult,
  MoonshotSignalsSnapshot,
  MoonshotStockSignal,
  MultiAgentReviewResult,
  Pending,
  PendingConfigChange,
  Position,
  PositionReassessment,
  PositionActionOutcome,
  PositionTrade,
  RiskLimit,
  RiskLimitKey,
  ResearchSuggestedSignal,
  SectorLimits,
  SectorLimitsUpdate,
  SavedResearchEvaluation,
  RunAgentReviewResult,
  AIWorkflowStatus,
  ShadowAgentReviewRunResult,
  SocialSentimentSnapshot,
  StrategyRoute,
  SystemHealthRow,
  TradePreview,
} from "../types";
import { normalizeAssetClass } from "../lib/assetClass";

// ---------------------------------------------------------------------
// DataSource contract
// ---------------------------------------------------------------------

export type DataSource = {
  /** Account metadata for the dashboard's multi-account strip. */
  listAccounts(): Promise<Account[]>;
  /** Pending approvals awaiting operator confirm. */
  listPending(): Promise<Pending[]>;
  /** Open positions (paper today; investment positions later). */
  listPositions(): Promise<Position[]>;
  listClosedStockTrades(args?: {
    brokerAccountId?: string;
    symbol?: string;
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<{ asOf: string; rows: ClosedStockTrade[] }>;
  listPositionReassessments(args?: {
    brokerAccountId?: string;
    symbol?: string;
    status?: "active" | "dismissed" | "confirmed" | "expired" | null;
    limit?: number;
  }): Promise<{ asOf: string; rows: PositionReassessment[] }>;
  runPositionReassessment(args?: {
    brokerAccountId?: string;
    symbol?: string;
    includeEquity?: boolean;
    includeCrypto?: boolean;
    includeForex?: boolean;
    persist?: boolean;
  }): Promise<{
    asOf: string;
    totalPositions: number;
    reviewed: number;
    persisted: number;
    skipped: number;
    degraded: number;
    rows: PositionReassessment[];
  }>;
  confirmPositionReassessmentAction(args: {
    reassessmentId: string;
    idempotencyKey: string;
  }): Promise<PositionReassessment>;
  dismissPositionReassessment(args: {
    reassessmentId: string;
    note?: string | null;
  }): Promise<PositionReassessment>;
  listPositionTrades(): Promise<PositionTrade[]>;
  listInvestments(): Promise<Investment[]>;
  listBaskets(): Promise<Basket[]>;
  listRiskLimits(brokerAccountId?: string): Promise<RiskLimit[]>;
  getRiskSnapshot(brokerAccountId: string): Promise<RiskSnapshot>;
  getSectorLimits(brokerAccountId: string): Promise<SectorLimits>;
  setSectorLimits(args: SectorLimitsUpdate): Promise<SectorLimits>;
  listPendingConfigChanges(): Promise<PendingConfigChange[]>;
  getAutoApprovalsConfig(): Promise<AutoApprovalsConfig>;
  setAutoApprovalsConfig(args: {
    enabled: boolean;
    minConfidence: number;
  }): Promise<AutoApprovalsConfig>;
  listStrategyRoutes(): Promise<StrategyRoute[]>;
  updateStrategyRoute(args: {
    strategyId: string;
    brokerAccountId: string;
    operatorNote?: string | null;
  }): Promise<StrategyRoute>;

  /** Phase 5 a.7 — Health-page backup panel. */
  listBackupTests(limit?: number): Promise<BackupTestsResponseWire>;
  verifyLatestBackup(): Promise<BackupVerifyResponseWire>;

  /** Phase 5 (b) b.2 — Health-page subsystem dots. */
  getHealth(): Promise<HealthResponse>;

  /** Phase 7a D8 — monthly AI cost status + quality-tier warning. */
  getAiCostStatus(): Promise<AiCostStatusWire>;

  /** Phase 8 — Dashboard crypto tile summary (engine status + top watchlist
   * rows). Resilient: returns null sub-fields when individual endpoints
   * are unreachable rather than throwing, so the dashboard can render
   * a partial tile when only one of the two crypto endpoints is up. */
  getCryptoSummary(): Promise<CryptoSummary>;
  listCryptoValidationRuns(limit?: number): Promise<CryptoValidationRun[]>;
  // Phase 9-UI: forex gate + pair reference reads (ForexScreen).
  getForexConfig(): Promise<ForexConfig>;
  listForexPairs(): Promise<ForexPair[]>;
  // Phase 9 setup: tastyfx demo-row registration state. The
  // registry-backed accounts list can't see the DISABLED row, so
  // this read is how the UI shows "registered, dormant until 9b".
  getForexAccount(): Promise<ForexAccount>;
  listMoonshotSignals(args?: MoonshotSignalsArgs): Promise<MoonshotSignalsSnapshot>;
  rankMoonshotTopTen(args?: MoonshotSignalsArgs): Promise<MoonshotAIRankResult>;

  /** Phase 5 (d) — Dashboard System tile. Maps /health subsystems
   * to SystemHealthRow[] for the "System" panel. */
  listSystemHealth(): Promise<SystemHealthRow[]>;

  /** Phase 5 (d) — Dashboard Recent Events panel. Returns the last
   * `limit` audit events (default 20) as EventRow[]. */
  listRecentEvents(limit?: number): Promise<EventRow[]>;

  listChartSymbols(): Promise<string[]>;
  /** `timeframe` defaults to "1D" (daily workspace); intraday
   * timeframes back the Day-trades live-window backfill. */
  getChartSnapshot(
    symbol: string,
    lookback?: number,
    timeframe?: ChartTimeframe,
  ): Promise<ChartSnapshot>;

  /** Phase 5 (d) Open-Position Management D4 — live P&L snapshot. */
  getLivePnL(): Promise<LivePnLSnapshot>;

  /** Phase 5 (d) D1 — protective stop tightening (UX §9.2.1). */
  tightenStop(args: {
    brokerAccountId: string;
    symbol: string;
    newStopPrice: string;
    preset?:
      | "break_even"
      | "plus_0_5r"
      | "plus_1r"
      | "trailing_2_atr"
      | "custom";
    idempotencyKey: string;
  }): Promise<PositionActionOutcome>;

  /** Phase 5 (d) D1 — target adjustment (UX §9.2.2). */
  adjustTarget(args: {
    brokerAccountId: string;
    symbol: string;
    newTargetPrice: string;
    idempotencyKey: string;
  }): Promise<PositionActionOutcome>;

  /** Phase 5 (d) D1 — quick stop-to-break-even (UX §9.2.4). */
  breakEven(args: {
    brokerAccountId: string;
    symbol: string;
    idempotencyKey: string;
  }): Promise<PositionActionOutcome>;

  /** Phase 5 (d) D1 — exit at market (UX §9.2.3). */
  exitAtMarket(args: {
    brokerAccountId: string;
    symbol: string;
    idempotencyKey: string;
  }): Promise<{ qty: number | string }>;

  adjustPositionQty(args: {
    brokerAccountId: string;
    symbol: string;
    targetRiskDollars: string;
    idempotencyKey: string;
  }): Promise<{
    oldQty: number | string;
    newQty: number | string;
    deltaQty: number | string;
    action: "add" | "trim";
    preview: TradePreview;
    exitResizeWarning: string | null;
  }>;

  /** Manual sell-limit exit for crypto positions. */
  exitAtLimit(args: {
    brokerAccountId: string;
    symbol: string;
    limitPrice: string;
    idempotencyKey: string;
  }): Promise<{
    qty: number | string;
    limitPrice: number | string;
    brokerOrderId: string | null;
  }>;

  /**
   * Trigger a full signal-generation + auto-approval pass against
   * the most-recent completed session's data. Equivalent to the
   * scheduled 09:35 ET tick but operator-initiated on demand.
   *
   * Returns a lightweight summary so the dashboard can show inline
   * feedback ("3 signals, 2 approvals queued") immediately.
   */
  generateSignals(): Promise<GenerateSignalsResult>;

  /** Generated signal archive, including signals not converted to approvals. */
  listSignalHistory(limit?: number): Promise<SignalHistoryRow[]>;

  /** Generated AI research report archive. */
  listResearchReports(
    limit?: number,
    symbol?: string,
  ): Promise<ResearchReportRow[]>;
  createResearchReport(args: {
    symbol: string;
    template?: "research_v1" | "deep_dive_v1";
    forceRegenerate?: boolean;
  }): Promise<ResearchReportCreateResult>;

  /** Saved inline Research Workbench evaluations. */
  listSavedResearchEvaluations(
    limit?: number,
    symbol?: string,
  ): Promise<SavedResearchEvaluation[]>;

  /** Research Workbench — run all equity strategies against a symbol. */
  evaluateSymbol(
    symbol: string,
    horizon: "day_swing" | "investment" | "both",
    asOf?: string,
  ): Promise<EvaluationResult>;

  /** Research Workbench — mark an evaluation as saved (accepted). */
  acceptEvaluation(evaluationId: string): Promise<void>;

  /** Research Workbench — create a pending approval from an evaluation. */
  createApprovalFromEvaluation(args: {
    evaluationId: string;
    brokerAccountId: string;
    sizedShares: string;
    accountEquitySnapshot: string;
  }): Promise<{ approvalId: string; symbol: string }>;

  /** Research Workbench — run the optional advisory multi-agent review. */
  runAgentReview(
    evaluationId: string,
    includeSocialSentiment: boolean,
    resumeWorkflowRunId?: string,
  ): Promise<RunAgentReviewResult>;

  /** Research Workbench — fetch the latest advisory agent review. */
  getAgentReview(evaluationId: string): Promise<MultiAgentReviewResult>;

  /** Research Workbench — run advisory agent reviews in shadow mode. */
  runShadowAgentReviews(args: {
    symbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    includeSocialSentiment?: boolean;
    resumeWorkflowRunId?: string;
  }): Promise<ShadowAgentReviewRunResult>;

  /** Research Workbench — fetch one workflow status. */
  getWorkflowStatus(workflowRunId: string): Promise<AIWorkflowStatus>;

  /** Research Workbench — fetch latest workflow status by lookup key. */
  getLatestWorkflowStatus(args: {
    workflowType: string;
    symbol?: string;
    feature: string;
    asOfDate?: string;
  }): Promise<AIWorkflowStatus>;

  /** Research Workbench — resume checkpointed batch research enrichment. */
  resumeBatchResearchEnrichment(workflowRunId: string): Promise<GenerateSignalsResult>;

  /** Research Workbench — fetch sanitized social-sentiment context. */
  getSocialSentiment(
    symbol: string,
    refresh?: boolean,
  ): Promise<SocialSentimentSnapshot>;

  /** Research Workbench — list advisory outcome lessons. */
  listDecisionReflections(
    limit?: number,
    symbol?: string,
  ): Promise<DecisionReflection[]>;

  /** Research Workbench — generate outcome lessons for saved evaluations. */
  generateDecisionReflections(args: {
    symbol?: string;
    holdingWindowDays?: number;
    benchmarkSymbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    sourceTypes?: string[];
  }): Promise<DecisionReflection[]>;

  /** Reprice a generated signal against the live quote and queue approval. */
  repriceSignalApproval(signal: GeneratedSignal, riskDollars?: string): Promise<string>;

  previewSignalApproval(
    signal: GeneratedSignal,
    riskDollars?: string,
  ): Promise<{ preview: TradePreview; actionable: boolean; issues: string[] }>;

  /** Review a pending approval against live confirm-time safety gates. */
  reviewApproval(approvalId: string): Promise<ApprovalReviewResponse>;
  resizeApproval(
    approvalId: string,
    riskDollars: string,
  ): Promise<{ approvalId: string; preview: TradePreview }>;

  /** Halt all new trade approvals (TDD §7.4 user-initiated halt). */
  haltAll(reason?: string): Promise<void>;

  /** Resume new trade approvals after a user-initiated halt. */
  resumeAll(): Promise<void>;

  /** Confirm a pending approval and submit its bracket order to broker.
   *
   * `liveRealMoneyAck` carries the operator's explicit real-money
   * acknowledgment (UX §8.7). The server requires it True for
   * trading_live accounts; it is omitted/false for paper. */
  confirmAndPlaceApproval(
    approvalId: string,
    idempotencyKey: string,
    liveRealMoneyAck?: boolean,
  ): Promise<ConfirmAndPlaceResponse>;

  /** True when the source returns fixtures rather than live data. */
  readonly usingFixtures: boolean;
};

export type MoonshotSignalsArgs = {
  asOf?: string;
  universe?: string;
  limit?: number;
  maxPrice?: number | string;
  forceRefresh?: boolean;
};

/** Summary returned by a generateSignals() call. */
export type GenerateSignalsResult = {
  signalsGenerated: number;
  approvalsCreated: number;
  approvalsSkipped: number;
  longSignals: number;
  shortSignals: number;
  hedgeWatchSignals: number;
  autoApprovalConfidenceThreshold: number;
  regime: string;
  failures: number;
  asOf: string;
  generatedAt: string;
  universeSize: number;
  signals: GeneratedSignal[];
  failureRows: SignalFailure[];
};

export type AutoApprovalsConfig = {
  enabled: boolean;
  minConfidence: number;
  updatedAt: string | null;
  updatedBy: string | null;
};

export type RiskSnapshot = {
  brokerAccountId: string;
  limitsConfigured: boolean;
  dailyLossUsedPct: number | null;
  weeklyLossUsedPct: number | null;
  openPositionsUsed: number | null;
  sectorConcentrationUsedPct: number | null;
  maxTradesPerDayUsed: number | null;
  topSector: string | null;
};

/** Phase 9-UI — the forex activation gate (read-only; Override 5
 * Tier A). Flips via manual operator UPDATE only. */
export type ForexConfig = {
  forexTradingEnabled: boolean;
  enabledAt: string | null;
  enabledBy: string | null;
};

/** Phase 9-UI — one fx_pairs reference row. pip/margin values stay
 * strings (display-only; the UI does no money math per TDD §15.1). */
export type ForexPair = {
  symbol: string;
  baseCurrency: string;
  quoteCurrency: string;
  pipSize: string;
  priceDecimalPlaces: number;
  minUnitSize: string;
  marginRequirementPct: string;
  isActive: boolean;
};

/** Phase 9 setup — tastyfx demo broker-account registration state.
 * registered=false → no row exists yet (configure in Config → Data).
 * enabled is always false pre-9b: the row is deliberately dormant.
 * The account identifier is server-masked to its last 4 characters
 * (it doubles as a keychain credential — full value never leaves
 * the backend). */
export type ForexAccount = {
  registered: boolean;
  brokerAccountId: string | null;
  accountType: string | null;
  externalIdLast4: string | null;
  displayName: string | null;
  enabled: boolean;
};

/** Phase 8 — Dashboard crypto tile summary. */
export type CryptoSummary = {
  engineStatus: {
    autoTradingEnabled: boolean;
    autoTradingDisabledReason: string | null;
    adapters: number;
    maxOpenTrades: number;
    maxDailyEntries: number;
    riskPerTradePct: string;
    exitIntentsByStatus: Record<string, number>;
  } | null;
  topWatchlist: Array<{
    symbol: string;
    proximity: number;
    distancePct: string | null;
    plan: string;
    blockedReason: string | null;
    regimeLabel: string | null;
  }>;
  watchlistUniverseSize: number;
  generatedAt: string | null;
};

export type CryptoValidationReportSummary = {
  periodStart: string | null;
  periodEnd: string | null;
  trades: number | null;
  totalPnl: string | null;
  profitFactor: string | null;
  maxDrawdown: string | null;
  signalsEvaluated: number | null;
  signalsGenerated: number | null;
  signalsWarmupSkipped: number | null;
  signalsPairFilterBlocked: number | null;
  signalsProtectionBlocked: number | null;
  signalsInvalidDropped: number | null;
};

/** Latest persisted crypto strategy validation artifacts. */
export type CryptoValidationRun = {
  id: string;
  strategyId: string;
  createdAt: string;
  periodStart: string;
  periodEnd: string;
  status: "passed" | "failed";
  passed: boolean;
  baselineProfitFactor: string | null;
  baselineTotalPnl: string | null;
  baselineTrades: number | null;
  lookaheadPassed: boolean | null;
  recursiveStable: boolean | null;
  walkForwardPassed: boolean | null;
  parameterSweepPromoted: boolean | null;
  dashboardSummary: {
    totalPnl: string | null;
    profitFactor: string | null;
    maxDrawdown: string | null;
    trades: number | null;
  };
  pairLocks: Array<{
    symbol: string;
    reason: string;
    lockedAt: string | null;
    expiresAt: string | null;
  }>;
  protectionTriggers: Array<{
    reason: string;
    count: number;
  }>;
  strategyAttribution: Array<{
    strategyId: string;
    trades: number;
    totalPnl: string;
    profitFactor: string;
  }>;
  lookaheadDetails: {
    reason: string | null;
    baseline: CryptoValidationReportSummary;
    entriesSuppressed: CryptoValidationReportSummary;
    exitsSuppressed: CryptoValidationReportSummary;
  };
  recursiveReports: Array<
    CryptoValidationReportSummary & { startupCandleCount: number | null }
  >;
  walkForwardReports: Array<
    CryptoValidationReportSummary & {
      trainStart: string | null;
      trainEnd: string | null;
      validationStart: string | null;
      validationEnd: string | null;
    }
  >;
  parameterSweepTrials: Array<{
    params: Record<string, unknown>;
    promoted: boolean;
    train: CryptoValidationReportSummary;
    validation: CryptoValidationReportSummary;
  }>;
};

export type GeneratedSignal = {
  signalId: string | null;
  symbol: string;
  companyName?: string | null;
  strategyId: string;
  assetClass?: "equity" | "crypto" | "forex";
  quantityStep?: string | null;
  minQuantity?: string | null;
  minNotional?: string | null;
  barDate: string;
  direction: "long" | "short";
  entry: number;
  stop: number;
  targets: number[];
  score: number;
  factors: string[];
  rrRatio: number;
  aiConfidence: number | null;
  aiRationale: string | null;
  aiRiskFlags: string[];
  aiModelId: string | null;
  researchEnrichment?: SignalResearchEnrichment | null;
  edgeScore: number | null;
  regimeFit: string | null;
  portfolioFit: string | null;
  approvalGateReason: string | null;
  signalRole: "entry" | "hedge_watch";
};

export type SignalResearchEnrichment = {
  status?: string;
  approvalAction?: string;
  evaluationId?: string;
  compositeScore?: number;
  confidence?: string;
  investmentStance?: string;
  tradeStance?: string;
  aiMemoStatus?: string;
  analystSummary?: string;
  error?: string;
};

export type SignalFailure = {
  symbol: string;
  errorType: string;
  detail: string;
};

export type SignalHistoryStatus =
  | "skipped"
  | "pending"
  | "expired"
  | "approved"
  | "placed";

export type SignalHistoryRow = {
  signalId: string;
  generatedAt: string;
  symbol: string;
  companyName?: string | null;
  strategyId: string;
  assetClass?: "equity" | "crypto" | "forex";
  barDate: string;
  direction: "long" | "short";
  timeframe: string;
  entry: number;
  stop: number;
  targets: number[];
  rrRatio: number;
  score: number;
  rationale: string;
  aiModelId: string | null;
  aiInputMode: string | null;
  researchEnrichment?: SignalResearchEnrichment | null;
  edgeScore: number | null;
  regimeFit: string | null;
  portfolioFit: string | null;
  approvalGateReason: string | null;
  signalRole: "entry" | "hedge_watch";
  approvalStatus: SignalHistoryStatus;
  approvalId: string | null;
  approvalRequestedAt: string | null;
  approvalExpiresAt: string | null;
  approvalConsumedAt: string | null;
};

export type ResearchReportRow = {
  id: string;
  symbol: string;
  requestedBy: string;
  requestedAt: string;
  completedAt: string | null;
  modelVersion: string;
  promptVersion: string;
  assetClass: string;
  primaryDocuments: Record<string, unknown>[];
  newsHeadlinesUsed: Record<string, unknown>[] | null;
  fundamentalsUsed: Record<string, unknown> | null;
  reportKind: string;
  sectionLabels: Record<string, string>;
  suggestedSignal: ResearchSuggestedSignal | null;
  reportText: string;
  reportSections: Record<string, string>;
  bullCaseLength: number;
  bearCaseLength: number;
  citationCount: number;
  primaryDocQuoteCount: number;
  validatorPassed: boolean;
  validatorFailures: string[];
  cost: number;
  cachedUntil: string;
  cachedExpired: boolean;
  supersededBy: string | null;
};

export type ResearchReportCreateResult = {
  id: string;
  symbol: string;
  reportKind: string;
  validatorPassed: boolean;
  cacheHit: boolean;
  cost: number;
  completedAt: string | null;
  cachedUntil: string;
  suggestedSignal: ResearchSuggestedSignal | null;
  validatorFailures: string[];
};

// ---------------------------------------------------------------------
// Toggle
// ---------------------------------------------------------------------

/** Read the build-time env flag. Returns true if the developer
 * has opted in to fixture mode via VITE_USE_FIXTURES=true.
 *
 * Production builds DEFAULT to live API and ignore accidental
 * VITE_USE_FIXTURES=true unless VITE_ALLOW_PROD_FIXTURES=true is
 * also set. Vite injects these flags at build time, so this guard
 * protects the deployed bundle even if a local .env file leaks into
 * the build environment.
 */
export function useFixtures(): boolean {
  // import.meta.env is Vite's typed env access. The non-null
  // coercion below is safe because Vite always populates env.
  const flag = import.meta.env.VITE_USE_FIXTURES;
  if (
    import.meta.env.PROD &&
    import.meta.env.VITE_ALLOW_PROD_FIXTURES !== "true"
  ) {
    return false;
  }
  return flag === "true" || flag === "1";
}

// ---------------------------------------------------------------------
// Fixture data source — extracted to ./fixtureDataSource.ts so its
// import graph (../fixtures/*) is dropped from production bundles
// via the dynamic import in createDataSource() below.
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// API data source — calls the backend.
// ---------------------------------------------------------------------
//
// ApiDataSource talks exclusively to the app process's HTTP API.
// It never falls back to fixtures: a failed endpoint surfaces as a
// thrown error (handled by useDataSource, which sets `error` /
// degrades optional reads). Fixture data lives only in
// FixtureDataSource, selected at build/dev time via VITE_USE_FIXTURES
// (see useFixtures above).

export class ApiDataSource implements DataSource {
  readonly usingFixtures = false as const;

  constructor(
    private readonly client: ApiClient,
  ) {}

  async listAccounts(): Promise<Account[]> {
    const wire = await this.client.listAccounts();
    return wire.map(_wireToAccount);
  }
  async listPending(): Promise<Pending[]> {
    const wire = await this.client.listPendingApprovals();
    return wire.map(_wireToPending);
  }
  async listPositions(): Promise<Position[]> {
    const wire = await this.client.listPositions();
    return wire.map(_wireToPosition);
  }
  async listClosedStockTrades(args: {
    brokerAccountId?: string;
    symbol?: string;
    from?: string;
    to?: string;
    limit?: number;
  } = {}): Promise<{ asOf: string; rows: ClosedStockTrade[] }> {
    const wire = await this.client.listClosedStockTrades(args);
    return _wireToClosedStockTrades(wire);
  }
  async listPositionReassessments(args: {
    brokerAccountId?: string;
    symbol?: string;
    status?: "active" | "dismissed" | "confirmed" | "expired" | null;
    limit?: number;
  } = {}): Promise<{ asOf: string; rows: PositionReassessment[] }> {
    const wire = await this.client.listPositionReassessments(args);
    return _wireToPositionReassessmentList(wire);
  }
  async runPositionReassessment(args: {
    brokerAccountId?: string;
    symbol?: string;
    includeEquity?: boolean;
    includeCrypto?: boolean;
    includeForex?: boolean;
    persist?: boolean;
  } = {}): Promise<{
    asOf: string;
    totalPositions: number;
    reviewed: number;
    persisted: number;
    skipped: number;
    degraded: number;
    rows: PositionReassessment[];
  }> {
    const wire = await this.client.runPositionReassessment(args);
    return {
      asOf: wire.asOf,
      totalPositions: wire.totalPositions,
      reviewed: wire.reviewed,
      persisted: wire.persisted,
      skipped: wire.skipped,
      degraded: wire.degraded,
      rows: wire.rows.map(_wireToPositionReassessment),
    };
  }
  async confirmPositionReassessmentAction(args: {
    reassessmentId: string;
    idempotencyKey: string;
  }): Promise<PositionReassessment> {
    const wire = await this.client.confirmPositionReassessmentAction(
      args.reassessmentId,
      args.idempotencyKey,
    );
    return _wireToPositionReassessment(wire);
  }
  async dismissPositionReassessment(args: {
    reassessmentId: string;
    note?: string | null;
  }): Promise<PositionReassessment> {
    const wire = await this.client.dismissPositionReassessment(
      args.reassessmentId,
      args.note,
    );
    return _wireToPositionReassessment(wire);
  }
  async listPositionTrades(): Promise<PositionTrade[]> {
    const wire = await this.client.listPositionTrades();
    return wire.map(_wireToPositionTrade);
  }
  async listInvestments(): Promise<Investment[]> {
    const wire = await this.client.listInvestments();
    return wire.map(_wireToInvestment);
  }
  async listBaskets(): Promise<Basket[]> {
    const wire = await this.client.listBaskets();
    return wire.map(_wireToBasket);
  }
  async getCryptoSummary(): Promise<CryptoSummary> {
    // Phase 8 — Dashboard tile composite of paper-engine status +
    // top-of-watchlist proximity. Both endpoints are best-effort:
    // a single failure (e.g., Coinbase keys missing → watchlist
    // endpoint 503) shouldn't blank the tile, just degrade it.
    let engineStatus: CryptoSummary["engineStatus"] = null;
    let watchlistRows: CryptoWatchlistAnalysisRowWire[] = [];
    let generatedAt: string | null = null;
    try {
      const status = await this.client.getCryptoPaperEngineStatus();
      engineStatus = {
        autoTradingEnabled: status.auto_trading_enabled,
        autoTradingDisabledReason:
          status.auto_trading_disabled_reason ?? null,
        adapters: status.registered_crypto_paper_adapters,
        maxOpenTrades: status.max_open_trades,
        maxDailyEntries: status.max_daily_entries,
        riskPerTradePct: status.risk_per_trade_pct,
        exitIntentsByStatus: status.exit_intents_by_status,
      };
    } catch {
      // Engine status unavailable; render the tile with partial data.
    }
    try {
      const wl = await this.client.getCryptoWatchlistAnalysis();
      watchlistRows = wl.rows;
      generatedAt = wl.generated_at;
    } catch {
      // Watchlist unavailable; tile shows "no data yet".
    }
    const topWatchlist = [...watchlistRows]
      .sort((a, b) => b.proximity - a.proximity)
      .slice(0, 3)
      .map((r) => ({
        symbol: r.symbol,
        proximity: r.proximity,
        distancePct: r.distance_pct,
        plan: r.plan,
        blockedReason: r.blocked_reason,
        regimeLabel: r.regime_label,
      }));
    return {
      engineStatus,
      topWatchlist,
      watchlistUniverseSize: watchlistRows.length,
      generatedAt,
    };
  }
  async listCryptoValidationRuns(limit: number = 5): Promise<CryptoValidationRun[]> {
    const wire = await this.client.listCryptoStrategyValidationRuns({ limit });
    return wire.runs.map(_wireToCryptoValidationRun);
  }
  async getForexConfig(): Promise<ForexConfig> {
    const wire = await this.client.getForexConfig();
    return {
      forexTradingEnabled: wire.forex_trading_enabled,
      enabledAt: wire.forex_trading_enabled_at ?? null,
      enabledBy: wire.forex_trading_enabled_by ?? null,
    };
  }
  async listForexPairs(): Promise<ForexPair[]> {
    const wire = await this.client.getForexPairs();
    return wire.pairs.map((p) => ({
      symbol: p.symbol,
      baseCurrency: p.base_currency,
      quoteCurrency: p.quote_currency,
      pipSize: p.pip_size,
      priceDecimalPlaces: p.price_decimal_places,
      minUnitSize: p.min_unit_size,
      marginRequirementPct: p.margin_requirement_pct,
      isActive: p.is_active,
    }));
  }
  async getForexAccount(): Promise<ForexAccount> {
    const wire = await this.client.getForexAccount();
    return {
      registered: wire.registered,
      brokerAccountId: wire.broker_account_id ?? null,
      accountType: wire.account_type ?? null,
      externalIdLast4: wire.external_id_last4 ?? null,
      displayName: wire.display_name ?? null,
      enabled: wire.enabled ?? false,
    };
  }
  async listMoonshotSignals(
    args: MoonshotSignalsArgs = {},
  ): Promise<MoonshotSignalsSnapshot> {
    const wire = await this.client.getMoonshotSignals(args);
    return _wireToMoonshotSignalsSnapshot(wire);
  }
  async rankMoonshotTopTen(
    args: MoonshotSignalsArgs = {},
  ): Promise<MoonshotAIRankResult> {
    const wire = await this.client.rankMoonshotTopTen(args);
    return _wireToMoonshotAIRankResult(wire);
  }
  async listRiskLimits(brokerAccountId?: string): Promise<RiskLimit[]> {
    const wire = await this.client.listRiskLimits(brokerAccountId);
    return wire.map(_wireToRiskLimit);
  }
  async getRiskSnapshot(brokerAccountId: string): Promise<RiskSnapshot> {
    const wire = await this.client.getRiskSnapshot(brokerAccountId);
    return _wireToRiskSnapshot(wire);
  }
  async getSectorLimits(brokerAccountId: string): Promise<SectorLimits> {
    const wire = await this.client.getSectorLimits(brokerAccountId);
    return _wireToSectorLimits(wire);
  }
  async setSectorLimits(args: SectorLimitsUpdate): Promise<SectorLimits> {
    const body: SectorLimitsUpdateBody = {
      broker_account_id: args.brokerAccountId,
      default_cap_pct: args.defaultCapPct,
      overrides: args.overrides,
    };
    const wire = await this.client.setSectorLimits(body);
    return _wireToSectorLimits(wire);
  }
  async listPendingConfigChanges(): Promise<PendingConfigChange[]> {
    const wire = await this.client.listPendingChanges();
    return wire.map(_wireToPendingConfigChange);
  }
  async getAutoApprovalsConfig(): Promise<AutoApprovalsConfig> {
    const wire = await this.client.getAutoApprovalsConfig();
    return _wireToAutoApprovalsConfig(wire);
  }
  async setAutoApprovalsConfig(args: {
    enabled: boolean;
    minConfidence: number;
  }): Promise<AutoApprovalsConfig> {
    const wire = await this.client.setAutoApprovalsConfig({
      enabled: args.enabled,
      min_confidence: args.minConfidence,
    });
    return _wireToAutoApprovalsConfig(wire);
  }

  async listStrategyRoutes(): Promise<StrategyRoute[]> {
    const rows = await this.client.listStrategyRoutes();
    return rows.map(_wireToStrategyRoute);
  }

  async updateStrategyRoute(args: {
    strategyId: string;
    brokerAccountId: string;
    operatorNote?: string | null;
  }): Promise<StrategyRoute> {
    const row = await this.client.updateStrategyRoute(args.strategyId, {
      broker_account_id: args.brokerAccountId,
      operator_note: args.operatorNote ?? null,
    });
    return _wireToStrategyRoute(row);
  }

  // Approval flow — these wrap the actual HTTP endpoints.
  async requestApproval(
    body: Parameters<ApiClient["requestApproval"]>[0],
  ): Promise<ApprovalResponse> {
    return this.client.requestApproval(body);
  }
  async confirmApproval(
    body: Parameters<ApiClient["confirmApproval"]>[0],
  ) {
    return this.client.confirmApproval(body);
  }
  async confirmAndPlaceApproval(
    approvalId: string,
    idempotencyKey: string,
    liveRealMoneyAck = false,
  ): Promise<ConfirmAndPlaceResponse> {
    return this.client.confirmAndPlaceApproval({
      approval_id: approvalId,
      idempotency_key: idempotencyKey,
      user_acknowledged_risk: true,
      user_acknowledged_gap_risk: true,
      user_acknowledged_live_real_money: liveRealMoneyAck,
    });
  }
  async reviewApproval(
    approvalId: string,
  ): Promise<ApprovalReviewResponse> {
    return this.client.reviewApproval(approvalId);
  }

  async resizeApproval(
    approvalId: string,
    riskDollars: string,
  ): Promise<{ approvalId: string; preview: TradePreview }> {
    const wire = await this.client.resizeApproval(approvalId, riskDollars);
    return {
      approvalId: wire.approval.approval_id,
      preview: _wireToTradePreview(wire.preview),
    };
  }

  // Phase 5 a.7 — Health-page backup panel.
  async listBackupTests(limit?: number): Promise<BackupTestsResponseWire> {
    return this.client.listBackupTests(limit);
  }
  async verifyLatestBackup(): Promise<BackupVerifyResponseWire> {
    return this.client.verifyLatestBackup();
  }
  // Phase 5 (b) b.2 — subsystem dots.
  async getHealth(): Promise<HealthResponse> {
    return this.client.getHealth();
  }

  // Phase 7a D8 — monthly AI cost status.
  async getAiCostStatus(): Promise<AiCostStatusWire> {
    return this.client.getAiCostStatus();
  }

  // Phase 5 (d) — Dashboard System tile.
  async listSystemHealth(): Promise<SystemHealthRow[]> {
    const health = await this.client.getHealth();
    return _healthToSystemRows(health);
  }

  // Phase 5 (d) — Dashboard Recent Events panel.
  async listRecentEvents(limit: number = 20): Promise<EventRow[]> {
    const wire = await this.client.getRecentEvents(limit);
    return wire.events.map(_wireToEventRow);
  }

  async listChartSymbols(): Promise<string[]> {
    const wire = await this.client.listChartSymbols();
    return wire.map((row) => row.symbol);
  }

  async getChartSnapshot(
    symbol: string,
    lookback: number = 260,
    timeframe: ChartTimeframe = "1D",
  ): Promise<ChartSnapshot> {
    const wire = await this.client.getChartSnapshot(
      symbol,
      lookback,
      timeframe,
    );
    return _wireToChartSnapshot(wire);
  }

  async getLivePnL(): Promise<LivePnLSnapshot> {
    const wire = await this.client.getLivePnL();
    return _wireToLivePnL(wire);
  }

  async tightenStop(args: {
    brokerAccountId: string;
    symbol: string;
    newStopPrice: string;
    preset?:
      | "break_even"
      | "plus_0_5r"
      | "plus_1r"
      | "trailing_2_atr"
      | "custom";
    idempotencyKey: string;
  }): Promise<PositionActionOutcome> {
    const wire = await this.client.tightenStop(
      args.brokerAccountId,
      args.symbol,
      {
        new_stop_price: args.newStopPrice,
        preset: args.preset ?? "custom",
        idempotency_key: args.idempotencyKey,
      },
    );
    return {
      symbol: wire.symbol,
      oldPrice: wire.old_stop_price,
      newPrice: wire.new_stop_price,
      brokerOrderId: wire.broker_order_id,
      modifiedAt: wire.modified_at,
    };
  }

  async adjustTarget(args: {
    brokerAccountId: string;
    symbol: string;
    newTargetPrice: string;
    idempotencyKey: string;
  }): Promise<PositionActionOutcome> {
    const wire = await this.client.adjustTarget(
      args.brokerAccountId,
      args.symbol,
      {
        new_target_price: args.newTargetPrice,
        idempotency_key: args.idempotencyKey,
      },
    );
    return {
      symbol: wire.symbol,
      oldPrice: wire.old_target_price,
      newPrice: wire.new_target_price,
      brokerOrderId: wire.broker_order_id,
      modifiedAt: wire.modified_at,
      loosened: wire.loosened,
    };
  }

  async breakEven(args: {
    brokerAccountId: string;
    symbol: string;
    idempotencyKey: string;
  }): Promise<PositionActionOutcome> {
    const wire = await this.client.breakEven(
      args.brokerAccountId,
      args.symbol,
      { idempotency_key: args.idempotencyKey },
    );
    return {
      symbol: wire.symbol,
      oldPrice: wire.old_stop_price,
      newPrice: wire.new_stop_price,
      brokerOrderId: wire.broker_order_id,
      modifiedAt: wire.modified_at,
    };
  }

  async exitAtMarket(args: {
    brokerAccountId: string;
    symbol: string;
    idempotencyKey: string;
  }): Promise<{ qty: number | string }> {
    const wire = await this.client.exitAtMarket(
      args.brokerAccountId,
      args.symbol,
      { idempotency_key: args.idempotencyKey },
    );
    return { qty: wire.qty };
  }

  async adjustPositionQty(args: {
    brokerAccountId: string;
    symbol: string;
    targetRiskDollars: string;
    idempotencyKey: string;
  }): Promise<{
    oldQty: number | string;
    newQty: number | string;
    deltaQty: number | string;
    action: "add" | "trim";
    preview: TradePreview;
    exitResizeWarning: string | null;
  }> {
    const wire = await this.client.adjustPositionQty(
      args.brokerAccountId,
      args.symbol,
      {
        target_risk_dollars: args.targetRiskDollars,
        idempotency_key: args.idempotencyKey,
      },
    );
    return {
      oldQty: wire.old_qty,
      newQty: wire.new_qty,
      deltaQty: wire.delta_qty,
      action: wire.action,
      preview: _wireToTradePreview(wire.preview),
      exitResizeWarning: wire.exit_resize_warning,
    };
  }

  async exitAtLimit(args: {
    brokerAccountId: string;
    symbol: string;
    limitPrice: string;
    idempotencyKey: string;
  }): Promise<{
    qty: number | string;
    limitPrice: number | string;
    brokerOrderId: string | null;
  }> {
    const wire = await this.client.exitAtLimit(
      args.brokerAccountId,
      args.symbol,
      {
        limit_price: args.limitPrice,
        idempotency_key: args.idempotencyKey,
      },
    );
    return {
      qty: wire.qty,
      limitPrice: wire.limit_price,
      brokerOrderId: wire.broker_order_id,
    };
  }

  async haltAll(reason = "operator_request"): Promise<void> {
    await this.client.postSystemHalt(reason);
  }

  async resumeAll(): Promise<void> {
    await this.client.postSystemResume();
  }

  // Operator-initiated signal generation.
  async generateSignals(): Promise<GenerateSignalsResult> {
    const wire: SignalsGenerateWireResponse =
      await this.client.generateSignals();
    return _wireToGenerateSignalsResult(wire);
  }

  async resumeBatchResearchEnrichment(
    workflowRunId: string,
  ): Promise<GenerateSignalsResult> {
    const wire: SignalsGenerateWireResponse =
      await this.client.generateSignals({
        resume_research_workflow_run_id: workflowRunId,
      });
    return _wireToGenerateSignalsResult(wire);
  }

  async listSignalHistory(limit = 100): Promise<SignalHistoryRow[]> {
    const wire = await this.client.listSignalHistory(limit);
    return (wire.signals ?? []).map(_wireToSignalHistory);
  }

  async listResearchReports(
    limit = 100,
    symbol?: string,
  ): Promise<ResearchReportRow[]> {
    const wire = await this.client.listResearchReports(limit, symbol);
    return (wire.reports ?? []).map(_wireToResearchReport);
  }

  async createResearchReport(args: {
    symbol: string;
    template?: "research_v1" | "deep_dive_v1";
    forceRegenerate?: boolean;
  }): Promise<ResearchReportCreateResult> {
    const wire = await this.client.createResearchReport(args);
    return _wireToResearchReportCreateResult(wire);
  }

  async listSavedResearchEvaluations(
    limit = 100,
    symbol?: string,
  ): Promise<SavedResearchEvaluation[]> {
    const wire = await this.client.listSavedResearchEvaluations(limit, symbol);
    return (wire.evaluations ?? []).map(_wireToSavedResearchEvaluation);
  }

  async evaluateSymbol(
    symbol: string,
    horizon: "day_swing" | "investment" | "both",
    asOf?: string,
  ): Promise<EvaluationResult> {
    const wire = await this.client.evaluateSymbol(symbol, horizon, asOf);
    return _wireToEvaluationResult(wire);
  }

  async acceptEvaluation(evaluationId: string): Promise<void> {
    await this.client.acceptEvaluation(evaluationId);
  }

  async createApprovalFromEvaluation(args: {
    evaluationId: string;
    brokerAccountId: string;
    sizedShares: string;
    accountEquitySnapshot: string;
  }): Promise<{ approvalId: string; symbol: string }> {
    const wire = await this.client.createApprovalFromEvaluation({
      evaluation_id: args.evaluationId,
      broker_account_id: args.brokerAccountId,
      sized_shares: args.sizedShares,
      account_equity_snapshot: args.accountEquitySnapshot,
    });
    return { approvalId: wire.approval_id, symbol: wire.symbol };
  }

  async runAgentReview(
    evaluationId: string,
    includeSocialSentiment: boolean,
    resumeWorkflowRunId?: string,
  ): Promise<RunAgentReviewResult> {
    const wire = await this.client.runAgentReview(
      evaluationId,
      includeSocialSentiment,
      resumeWorkflowRunId,
    );
    return _wireToRunAgentReviewResult(wire);
  }

  async getAgentReview(
    evaluationId: string,
  ): Promise<MultiAgentReviewResult> {
    const wire = await this.client.getAgentReview(evaluationId);
    return _wireToMultiAgentReview(wire);
  }

  async runShadowAgentReviews(args: {
    symbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    includeSocialSentiment?: boolean;
    resumeWorkflowRunId?: string;
  }): Promise<ShadowAgentReviewRunResult> {
    const wire = await this.client.runShadowAgentReviews({
      symbol: args.symbol,
      limit: args.limit,
      accepted_only: args.acceptedOnly,
      include_social_sentiment: args.includeSocialSentiment,
      resume_workflow_run_id: args.resumeWorkflowRunId,
    });
    return _wireToShadowAgentReviewRunResult(wire);
  }

  async getWorkflowStatus(workflowRunId: string): Promise<AIWorkflowStatus> {
    const wire = await this.client.getWorkflowStatus(workflowRunId);
    return _wireToWorkflowStatus(wire);
  }

  async getLatestWorkflowStatus(args: {
    workflowType: string;
    symbol?: string;
    feature: string;
    asOfDate?: string;
  }): Promise<AIWorkflowStatus> {
    const wire = await this.client.getLatestWorkflowStatus({
      workflow_type: args.workflowType,
      symbol: args.symbol,
      feature: args.feature,
      as_of_date: args.asOfDate,
    });
    return _wireToWorkflowStatus(wire);
  }

  async getSocialSentiment(
    symbol: string,
    refresh = false,
  ): Promise<SocialSentimentSnapshot> {
    const wire = await this.client.getSocialSentiment(symbol, refresh);
    return _wireToSocialSentimentSnapshot(wire);
  }

  async listDecisionReflections(
    limit = 20,
    symbol?: string,
  ): Promise<DecisionReflection[]> {
    const wire = await this.client.listDecisionReflections(limit, symbol);
    return (wire.reflections ?? []).map(_wireToDecisionReflection);
  }

  async generateDecisionReflections(args: {
    symbol?: string;
    holdingWindowDays?: number;
    benchmarkSymbol?: string;
    limit?: number;
    acceptedOnly?: boolean;
    sourceTypes?: string[];
  }): Promise<DecisionReflection[]> {
    const wire = await this.client.generateDecisionReflections({
      symbol: args.symbol,
      holding_window_days: args.holdingWindowDays,
      benchmark_symbol: args.benchmarkSymbol,
      limit: args.limit,
      accepted_only: args.acceptedOnly,
      source_types: args.sourceTypes,
    });
    return (wire.reflections ?? []).map(_wireToDecisionReflection);
  }

  async repriceSignalApproval(
    signal: GeneratedSignal,
    riskDollars?: string,
  ): Promise<string> {
    const body: ApprovalRepriceRequestBody = {
      signal_id: signal.signalId,
      symbol: signal.symbol,
      strategy_id: signal.strategyId,
      bar_date: signal.barDate || null,
      direction: signal.direction,
      entry_price: String(signal.entry),
      stop_price: String(signal.stop),
      target_prices: signal.targets.map((target) => String(target)),
      rr_ratio: Number.isFinite(signal.rrRatio) ? String(signal.rrRatio) : null,
      score: signal.score,
      factors: signal.factors,
      ai_confidence: signal.aiConfidence,
      ai_rationale: signal.aiRationale,
      ai_risk_flags: signal.aiRiskFlags,
      ai_model_id: signal.aiModelId,
      risk_dollars: riskDollars ?? null,
    };
    const response = await this.client.repriceApproval(body);
    return response.approval.approval_id;
  }

  async previewSignalApproval(
    signal: GeneratedSignal,
    riskDollars?: string,
  ): Promise<{ preview: TradePreview; actionable: boolean; issues: string[] }> {
    const response = await this.client.previewApproval({
      signal_id: signal.signalId,
      symbol: signal.symbol,
      strategy_id: signal.strategyId,
      bar_date: signal.barDate || null,
      direction: signal.direction,
      entry_price: String(signal.entry),
      stop_price: String(signal.stop),
      target_prices: signal.targets.map((target) => String(target)),
      rr_ratio: Number.isFinite(signal.rrRatio) ? String(signal.rrRatio) : null,
      score: signal.score,
      factors: signal.factors,
      ai_confidence: signal.aiConfidence,
      ai_rationale: signal.aiRationale,
      ai_risk_flags: signal.aiRiskFlags,
      ai_model_id: signal.aiModelId,
      risk_dollars: riskDollars ?? null,
    });
    return {
      preview: _wireToTradePreview(response.preview),
      actionable: response.actionable,
      issues: response.issues.map((issue) => issue.detail),
    };
  }
}


// ---------------------------------------------------------------------
// Wire-shape → UI-shape converters
// ---------------------------------------------------------------------
//
// Numeric fields cross the wire as Decimal-as-string to preserve
// precision. The UI's existing types use ``number`` for display
// math (formatters round for human reading), so we parse here.

// Wire-key to UI RiskLimitKey mapping.
const _WIRE_TO_UI_RISK_KEY: Record<string, RiskLimitKey> = {
  risk_per_trade: "per_trade_risk",
  max_position: "max_position",
  max_open_positions: "max_open",
  daily_loss: "daily_loss",
  weekly_loss: "weekly_loss",
  sector_concentration: "sector_concentration",
  // Dataclass field names from /pending-changes wire.
  risk_per_trade_pct: "per_trade_risk",
  max_position_pct: "max_position",
  daily_loss_limit_pct: "daily_loss",
  weekly_loss_limit_pct: "weekly_loss",
  sector_concentration_pct: "sector_concentration",
};

function _wireToAccount(w: AccountWire): Account {
  // The wire's broker_account_id is a UUID; the UI's `Account.id`
  // is a domain identifier ("paper" / "live" / "invest"). Map by
  // broker name + label until the registry returns labelled IDs.
  const kind: Account["kind"] =
    w.account_type === "trading_live"
      ? "trading-live"
      : w.account_type === "investment"
        ? "investment"
        : w.broker.toLowerCase().includes("live")
          ? "trading-live"
          : w.broker.toLowerCase().includes("invest")
            ? "investment"
            : "trading-paper";
  const id: Account["id"] =
    kind === "trading-live" ? "live" : kind === "investment" ? "invest" : "paper";
  return {
    id,
    brokerAccountId: w.broker_account_id,
    kind,
    label: w.label,
    broker: w.broker,
    assetClass: w.asset_class ?? "equity",
    mask: w.mask,
    equity: Number(w.equity),
    buyingPower: Number(w.buying_power),
    todayPL: Number(w.today_pnl),
    todayPLPct: Number(w.equity) > 0 ? Number(w.today_pnl) / Number(w.equity) : 0,
    brokerEquity:
      w.broker_equity !== null && w.broker_equity !== undefined
        ? Number(w.broker_equity)
        : undefined,
    balanceSource: w.balance_source ?? "broker",
    sleeveAllocation:
      w.sleeve_allocation !== null && w.sleeve_allocation !== undefined
        ? Number(w.sleeve_allocation)
        : undefined,
    sleeveEquity:
      w.sleeve_equity !== null && w.sleeve_equity !== undefined
        ? Number(w.sleeve_equity)
        : undefined,
    sleeveCash:
      w.sleeve_cash !== null && w.sleeve_cash !== undefined
        ? Number(w.sleeve_cash)
        : undefined,
    sleeveRealizedPL:
      w.sleeve_realized_pnl !== null && w.sleeve_realized_pnl !== undefined
        ? Number(w.sleeve_realized_pnl)
        : undefined,
    sleeveUnrealizedPL:
      w.sleeve_unrealized_pnl !== null &&
      w.sleeve_unrealized_pnl !== undefined
        ? Number(w.sleeve_unrealized_pnl)
        : undefined,
    sinceStartPL: 0,
    strategiesBound: 0,
    reconciledSec: 0,
    status: w.trading_blocked ? "degraded" : "connected",
  };
}

function _wireToCryptoValidationRun(
  w: CryptoValidationRunWire,
): CryptoValidationRun {
  const dashboard = recordAt(w.artifacts, "dashboard");
  const dashboardSummary = recordAt(dashboard, "summary");
  const lookahead = recordAt(w.artifacts, "lookahead");
  const recursive = recordAt(w.artifacts, "recursive");
  const walkForward = recordAt(w.artifacts, "walk_forward");
  const parameterSweep = recordAt(w.artifacts, "parameter_sweep");
  return {
    id: w.validation_run_id,
    strategyId: w.strategy_id,
    createdAt: w.created_at,
    periodStart: w.period_start,
    periodEnd: w.period_end,
    status: w.status,
    passed: w.passed,
    baselineProfitFactor: stringAt(w.summary, "baseline_profit_factor"),
    baselineTotalPnl: stringAt(w.summary, "baseline_total_pnl"),
    baselineTrades: numberAt(w.summary, "baseline_trades"),
    lookaheadPassed: booleanAt(w.summary, "lookahead_passed"),
    recursiveStable: booleanAt(w.summary, "recursive_stable"),
    walkForwardPassed: booleanAt(w.summary, "walk_forward_passed"),
    parameterSweepPromoted: booleanAt(w.summary, "parameter_sweep_promoted"),
    dashboardSummary: {
      totalPnl: stringAt(dashboardSummary, "total_pnl"),
      profitFactor: stringAt(dashboardSummary, "profit_factor"),
      maxDrawdown: stringAt(dashboardSummary, "max_drawdown"),
      trades: numberAt(dashboardSummary, "trades"),
    },
    pairLocks: arrayAt(dashboard, "pair_locks").map((row) => {
      const record = asRecord(row);
      return {
        symbol: stringAt(record, "symbol") ?? "",
        reason: stringAt(record, "reason") ?? "",
        lockedAt: stringAt(record, "locked_at"),
        expiresAt: stringAt(record, "expires_at"),
      };
    }).filter((row) => row.symbol !== ""),
    protectionTriggers: arrayAt(dashboard, "protection_triggers").map((row) => {
      const record = asRecord(row);
      return {
        reason: stringAt(record, "reason") ?? "unknown",
        count: numberAt(record, "count") ?? 0,
      };
    }),
    strategyAttribution: arrayAt(dashboard, "strategy_attribution").map((row) => {
      const record = asRecord(row);
      return {
        strategyId: stringAt(record, "strategy_id") ?? "unknown",
        trades: numberAt(record, "trades") ?? 0,
        totalPnl: stringAt(record, "total_pnl") ?? "0",
        profitFactor: stringAt(record, "profit_factor") ?? "0",
      };
    }),
    lookaheadDetails: {
      reason: stringAt(lookahead, "reason"),
      baseline: validationReportSummary(recordAt(lookahead, "baseline")),
      entriesSuppressed: validationReportSummary(
        recordAt(lookahead, "entries_suppressed"),
      ),
      exitsSuppressed: validationReportSummary(
        recordAt(lookahead, "exits_suppressed"),
      ),
    },
    recursiveReports: arrayAt(recursive, "reports").map((row) => {
      const record = asRecord(row);
      return {
        startupCandleCount: numberAt(record, "startup_candle_count"),
        ...validationReportSummary(record),
      };
    }),
    walkForwardReports: arrayAt(walkForward, "window_reports").map((row) => {
      const record = asRecord(row);
      return {
        trainStart: stringAt(record, "train_start"),
        trainEnd: stringAt(record, "train_end"),
        validationStart: stringAt(record, "validation_start"),
        validationEnd: stringAt(record, "validation_end"),
        ...validationReportSummary(record),
      };
    }),
    parameterSweepTrials: arrayAt(parameterSweep, "trial_reports").map((row) => {
      const record = asRecord(row);
      return {
        params: recordAt(record, "params"),
        promoted: booleanAt(record, "promoted") ?? false,
        train: validationReportSummary(recordAt(record, "train")),
        validation: validationReportSummary(recordAt(record, "validation")),
      };
    }),
  };
}

function validationReportSummary(
  record: Record<string, unknown>,
): CryptoValidationReportSummary {
  return {
    periodStart: stringAt(record, "period_start"),
    periodEnd: stringAt(record, "period_end"),
    trades: numberAt(record, "trades"),
    totalPnl: stringAt(record, "total_pnl"),
    profitFactor: stringAt(record, "profit_factor"),
    maxDrawdown: stringAt(record, "max_drawdown"),
    signalsEvaluated: numberAt(record, "signals_evaluated"),
    signalsGenerated: numberAt(record, "signals_generated"),
    signalsWarmupSkipped: numberAt(record, "signals_warmup_skipped"),
    signalsPairFilterBlocked: numberAt(record, "signals_pair_filter_blocked"),
    signalsProtectionBlocked: numberAt(record, "signals_protection_blocked"),
    signalsInvalidDropped: numberAt(record, "signals_invalid_dropped"),
  };
}

function _wireToMoonshotSignalsSnapshot(
  w: MoonshotSignalsResponseWire,
): MoonshotSignalsSnapshot {
  return {
    generatedAt: w.generated_at,
    universe: w.universe,
    sample: w.sample,
    providerStatuses: w.provider_statuses.map((status) => ({ ...status })),
    rows: w.rows.map(_wireToMoonshotStockSignal),
  };
}

function _wireToMoonshotStockSignal(
  w: MoonshotStockSignalWire,
): MoonshotStockSignal {
  return {
    ticker: w.ticker,
    companyName: w.company_name,
    sector: w.sector,
    riskTier: w.risk_tier,
    rank: Number(w.rank ?? 0),
    researchPriorityScore: Number(w.research_priority_score ?? 0),
    rankReasons: Array.isArray(w.rank_reasons) ? [...w.rank_reasons] : [],
    dataQuality: w.data_quality ?? "limited",
    currentPrice: Number(w.current_price),
    percentChange: Number(w.percent_change),
    unusualVolumeRatio: Number(w.unusual_volume_ratio),
    nextEarningsDate: w.next_earnings_date,
    catalysts: w.catalysts.map((catalyst) => ({ ...catalyst })),
    recentNews: w.recent_news.map((item) => ({ ...item })),
    publicDisclosures: { ...w.public_disclosures },
    riskRead: w.risk_read,
    sampleAsOf: w.sample_as_of,
    dataAsOf: w.data_as_of,
    dataDelay: w.data_delay,
  };
}

function _wireToMoonshotAIRankResult(
  w: MoonshotAIRankResponseWire,
): MoonshotAIRankResult {
  return {
    generatedAt: w.generated_at,
    universe: w.universe,
    status: w.status,
    detail: w.detail,
    modelId: w.model_id,
    sourceRankLimit: w.source_rank_limit,
    rows: w.rows.map((row) => ({
      ticker: row.ticker,
      deterministicRank: row.deterministic_rank,
      aiRank: row.ai_rank,
      aiScore: row.ai_score,
      aiReason: row.ai_reason,
      confidence: row.confidence,
      riskFlags: [...row.risk_flags],
    })),
  };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function recordAt(
  source: Record<string, unknown>,
  key: string,
): Record<string, unknown> {
  return asRecord(source[key]);
}

function arrayAt(source: Record<string, unknown>, key: string): unknown[] {
  return Array.isArray(source[key]) ? source[key] : [];
}

function stringAt(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}

function numberAt(source: Record<string, unknown>, key: string): number | null {
  const value = source[key];
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function booleanAt(
  source: Record<string, unknown>,
  key: string,
): boolean | null {
  const value = source[key];
  return typeof value === "boolean" ? value : null;
}

function _wireToSectorLimits(w: SectorLimitsWire): SectorLimits {
  return {
    brokerAccountId: w.broker_account_id,
    defaultCapPct: Number(w.defaultCapPct),
    overrides: Object.fromEntries(
      Object.entries(w.overrides).map(([sector, cap]) => [sector, Number(cap)]),
    ),
    sectors: w.sectors.map((row) => ({
      sector: row.sector,
      capPct: Number(row.capPct),
      usesDefault: row.usesDefault,
    })),
    updatedAt: w.updatedAt ?? null,
  };
}

function _wireToAutoApprovalsConfig(
  w: AutoApprovalsConfigWire,
): AutoApprovalsConfig {
  return {
    enabled: w.enabled,
    minConfidence: w.min_confidence,
    updatedAt: w.updated_at,
    updatedBy: w.updated_by,
  };
}

function _wireToStrategyRoute(w: StrategyRouteWire): StrategyRoute {
  return {
    strategyId: w.strategy_id,
    baseStrategyId: w.base_strategy_id,
    name: w.name,
    version: w.version,
    timeframe: w.timeframe,
    status: w.status,
    brokerAccountId: w.broker_account_id,
    accountType: w.account_type,
    assetClass: w.asset_class,
    accountLabel: w.account_label,
    routeTargets: w.route_targets.map((target) => ({
      brokerAccountId: target.broker_account_id,
      accountType: target.account_type,
      assetClass: target.asset_class,
      label: target.label,
      enabled: target.enabled,
      blockedReason: target.blocked_reason,
    })),
  };
}

function _wireToTradePreview(w: TradePreviewWire): TradePreview {
  return {
    derivedQty: Number(w.derived_qty),
    actualRiskDollars: Number(w.actual_risk_dollars),
    targetGainDollars: Number(w.target_gain_dollars),
    riskRewardRatio: Number(w.risk_reward_ratio),
    basisPrice: Number(w.basis_price),
    stopPrice: Number(w.stop_price),
    targetPrice: Number(w.target_price),
    riskPerShare: Number(w.risk_per_share),
    gainPerShare: Number(w.gain_per_share),
  };
}

function _pendingTradePreview(args: {
  side: Pending["side"];
  qty: number;
  entry: number;
  stop: number;
  target: number | undefined;
}): TradePreview | null {
  const target = args.target;
  if (
    target === undefined ||
    !Number.isFinite(args.qty) ||
    !Number.isFinite(args.entry) ||
    !Number.isFinite(args.stop) ||
    !Number.isFinite(target) ||
    args.qty <= 0
  ) {
    return null;
  }
  const riskPerShare =
    args.side === "short" ? args.stop - args.entry : args.entry - args.stop;
  const gainPerShare =
    args.side === "short" ? args.entry - target : target - args.entry;
  if (riskPerShare <= 0 || gainPerShare <= 0) {
    return null;
  }
  const actualRiskDollars = riskPerShare * args.qty;
  const targetGainDollars = gainPerShare * args.qty;
  return {
    derivedQty: args.qty,
    actualRiskDollars,
    targetGainDollars,
    riskRewardRatio: targetGainDollars / actualRiskDollars,
    basisPrice: args.entry,
    stopPrice: args.stop,
    targetPrice: target,
    riskPerShare,
    gainPerShare,
  };
}

function _numMeta(meta: Record<string, unknown>, key: string): number | null {
  const raw = meta[key];
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function _stringMeta(
  meta: Record<string, unknown>,
  key: string,
): string | null {
  const raw = meta[key];
  return typeof raw === "string" && raw.trim() !== "" ? raw : null;
}

function _stringListMeta(
  meta: Record<string, unknown>,
  key: string,
): string[] {
  const raw = meta[key];
  if (!Array.isArray(raw)) return [];
  return raw.filter((item): item is string => typeof item === "string");
}

function _wireToResearchEnrichment(
  raw: unknown,
): SignalResearchEnrichment | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  const compositeScore = value.composite_score;
  return {
    status: typeof value.status === "string" ? value.status : undefined,
    approvalAction:
      typeof value.approval_action === "string"
        ? value.approval_action
        : undefined,
    evaluationId:
      typeof value.evaluation_id === "string" ? value.evaluation_id : undefined,
    compositeScore:
      typeof compositeScore === "number"
        ? compositeScore
        : typeof compositeScore === "string"
          ? Number(compositeScore)
          : undefined,
    confidence:
      typeof value.confidence === "string" ? value.confidence : undefined,
    investmentStance:
      typeof value.investment_stance === "string"
        ? value.investment_stance
        : undefined,
    tradeStance:
      typeof value.trade_stance === "string" ? value.trade_stance : undefined,
    aiMemoStatus:
      typeof value.ai_memo_status === "string"
        ? value.ai_memo_status
        : undefined,
    analystSummary:
      typeof value.analyst_summary === "string"
        ? value.analyst_summary
        : undefined,
    error: typeof value.error === "string" ? value.error : undefined,
  };
}

function _limitChecksMeta(meta: Record<string, unknown>): LimitCheckRow[] {
  const raw = meta.limit_checks;
  if (!Array.isArray(raw)) return [];
  const allowed = new Set<LimitCheckRow["status"]>([
    "pass",
    "warn",
    "fail",
    "not_applicable",
  ]);
  return raw.flatMap((item): LimitCheckRow[] => {
    if (typeof item !== "object" || item === null) return [];
    const row = item as Record<string, unknown>;
    const name = typeof row.name === "string" ? row.name : null;
    const status = typeof row.status === "string" ? row.status : null;
    if (name === null || !allowed.has(status as LimitCheckRow["status"])) {
      return [];
    }
    return [
      {
        name,
        status: status as LimitCheckRow["status"],
        value:
          typeof row.value === "string" || typeof row.value === "number"
            ? String(row.value)
            : undefined,
        limit:
          typeof row.limit === "string" || typeof row.limit === "number"
            ? String(row.limit)
            : undefined,
        detail: typeof row.detail === "string" ? row.detail : undefined,
      },
    ];
  });
}

// Map the backend's broker-account type onto the UI's AccountId.
// Unknown/absent types default to "paper" — the safest assumption is
// wrong-but-loud only in the paper direction; the backend now sends
// account_type explicitly (2026-06-10 safety review, finding 1/5) so
// trading_live rows engage the live danger-ack machinery.
function _accountFromWire(
  accountType: PendingApprovalWire["account_type"],
): { account: Pending["account"]; accountLabel: string } {
  if (accountType === "trading_live") {
    return { account: "live", accountLabel: "Trading Live" };
  }
  if (accountType === "investment") {
    return { account: "invest", accountLabel: "Investment" };
  }
  return { account: "paper", accountLabel: "Trading Paper" };
}

// Display label for the originating signal's timeframe. Falls back to
// the historical "Swing" default when the backend recorded none.
function _timeframeFromWire(raw: string | null | undefined): string {
  if (typeof raw !== "string" || raw.trim() === "") return "Swing";
  const lowered = raw.trim().toLowerCase();
  if (lowered.includes("swing")) return "Swing";
  if (lowered.includes("position")) return "Position";
  if (lowered === "day" || lowered === "intraday") return "Day";
  return raw.trim();
}

// Server-computed trade preview persisted by the resize endpoint in
// signal_metadata.size_override.preview (TradePreviewWire-shaped
// strings). Preferred over client-side float math when present.
function _serverPreviewMeta(
  meta: Record<string, unknown>,
): TradePreview | null {
  const override = meta.size_override;
  if (typeof override !== "object" || override === null) return null;
  const preview = (override as Record<string, unknown>).preview;
  if (typeof preview !== "object" || preview === null) return null;
  const p = preview as Record<string, unknown>;
  const num = (key: string): number | null => {
    const rawValue = p[key];
    if (typeof rawValue !== "string" && typeof rawValue !== "number") {
      return null;
    }
    const n = Number(rawValue);
    return Number.isFinite(n) ? n : null;
  };
  const derivedQty = num("derived_qty");
  const actualRiskDollars = num("actual_risk_dollars");
  const targetGainDollars = num("target_gain_dollars");
  const riskRewardRatio = num("risk_reward_ratio");
  const basisPrice = num("basis_price");
  const stopPrice = num("stop_price");
  const targetPrice = num("target_price");
  const riskPerShare = num("risk_per_share");
  const gainPerShare = num("gain_per_share");
  if (
    derivedQty === null ||
    actualRiskDollars === null ||
    targetGainDollars === null ||
    riskRewardRatio === null ||
    basisPrice === null ||
    stopPrice === null ||
    targetPrice === null ||
    riskPerShare === null ||
    gainPerShare === null
  ) {
    return null;
  }
  return {
    derivedQty,
    actualRiskDollars,
    targetGainDollars,
    riskRewardRatio,
    basisPrice,
    stopPrice,
    targetPrice,
    riskPerShare,
    gainPerShare,
  };
}

function _wireToPending(w: PendingApprovalWire): Pending {
  // The backend's "side" is buy/sell; the UI's is long/short.
  // For long-only paper trading these are 1:1 (buy=long).
  const side: Pending["side"] = w.side === "buy" ? "long" : "short";
  const meta = w.signal_metadata ?? {};
  const detScore = _numMeta(meta, "score");
  const aiScore = _numMeta(meta, "ai_confidence");
  const factors = _stringListMeta(meta, "factors");
  const riskFlags = _stringListMeta(meta, "ai_risk_flags");
  const limitChecks = _limitChecksMeta(meta);
  const research = _wireToResearchEnrichment(meta.research_enrichment);
  const aiRationale = _stringMeta(meta, "ai_rationale");
  const modelId = _stringMeta(meta, "ai_model_id");
  const strategyId = _stringMeta(meta, "strategy_id") ?? "unknown_strategy";
  const scoreKnown = detScore !== null;
  const qty = Number(w.sized_shares);
  const entry = Number(w.sized_entry_price);
  const stop = w.sized_stop_price !== null ? Number(w.sized_stop_price) : 0;
  const targets = w.sized_target_prices.map((t) => Number(t));
  // Prefer the server's Decimal-computed preview (resize endpoint
  // persists it in signal_metadata.size_override.preview). The
  // client-side float computation below is a FALLBACK ONLY for rows
  // that never carried a server preview (2026-06-10 safety review,
  // finding 8). The approval modal additionally refreshes this from
  // GET /approvals/review/{id} before any ack is shown.
  const serverPreview = _serverPreviewMeta(meta);
  const preview =
    serverPreview ??
    _pendingTradePreview({
      side,
      qty,
      entry,
      stop,
      target: targets[0],
    });
  const riskPerShare = side === "short" ? stop - entry : entry - stop;
  const clientDollarRisk = Math.max(0, riskPerShare * qty);
  const dollarRisk = serverPreview?.actualRiskDollars ?? clientDollarRisk;
  const { account, accountLabel } = _accountFromWire(w.account_type);
  const expiresInSec = Math.max(
    0,
    Math.floor(
      (new Date(w.token_expires_at).getTime() - Date.now()) / 1000,
    ),
  );
  return {
    approvalId: w.approval_id,
    brokerAccountId: w.broker_account_id,
    assetClass: normalizeAssetClass(w.asset_class),
    sym: w.symbol,
    companyName: w.company_name ?? null,
    side,
    strategy: strategyId,
    account,
    accountLabel,
    entry,
    stop,
    targets,
    qty,
    risk: dollarRisk,
    riskPct: (() => {
      const equity = Number(w.account_equity_snapshot);
      if (equity <= 0) return 0;
      return (dollarRisk / equity) * 100;
    })(),
    preview,
    currentPricePreview: null,
    ai: aiScore ?? 0,
    det: detScore ?? 0,
    scoreKnown,
    aiModelId: modelId,
    // Base mode is "standard"; resolveMode (ui/src/lib/approval.ts)
    // derives the effective mode from account + PF gap as designed
    // (live forbids express; gap > 30% upgrades to careful).
    mode: "standard",
    timeframe: _timeframeFromWire(
      w.timeframe ?? _stringMeta(meta, "timeframe"),
    ),
    gapRisk: 0,
    gapRiskPct: 0,
    reason: [
      aiRationale ??
        (scoreKnown
          ? factors.join(", ") || "Deterministic score recorded."
          : "Scoring not recorded on this approval row."),
      research?.status === "ready"
        ? `Research ${research.approvalAction ?? "neutral"}: ${
            research.investmentStance ?? "unknown"
          }/${research.tradeStance ?? "unknown"}${
            research.compositeScore === undefined
              ? ""
              : ` (${research.compositeScore})`
          }`
        : research?.status === "failed"
          ? "Research unavailable"
          : null,
    ]
      .filter(Boolean)
      .join(" · "),
    cited:
      factors.length > 0
        ? factors.join(", ")
        : modelId
          ? `model ${modelId}`
          : "approval-only record",
    aiRiskFlags: riskFlags,
    limitChecks,
    strategyAge: "",
    expiresInSec,
    expressEligible: false,
    agentReview: w.agent_review ? _wireToMultiAgentReview(w.agent_review) : null,
  };
}

function _wireToGenerateSignalsResult(
  wire: SignalsGenerateWireResponse,
): GenerateSignalsResult {
  return {
    signalsGenerated: wire.summary.signals_generated,
    approvalsCreated: wire.approvals_created.length,
    approvalsSkipped: wire.approvals_skipped,
    longSignals: wire.summary.long_signals ?? 0,
    shortSignals: wire.summary.short_signals ?? 0,
    hedgeWatchSignals: wire.summary.hedge_watch_signals ?? 0,
    autoApprovalConfidenceThreshold:
      wire.summary.auto_approval_confidence_threshold ?? 70,
    regime: wire.summary.regime,
    failures: wire.summary.failures,
    asOf: wire.as_of,
    generatedAt: wire.generated_at,
    universeSize: wire.summary.universe_size,
    signals: (wire.signals ?? []).map(_wireToGeneratedSignal),
    failureRows: (wire.failures ?? []).map(_wireToSignalFailure),
  };
}

function _wireToPosition(w: PositionWire): Position {
  const rawQty = Number(w.qty);
  const qty = Math.abs(rawQty);
  const marketValue = Number(w.market_value);
  const entry = Number(w.avg_entry_price);
  const side = w.side === "sell" ? "short" : "long";
  const hasMarketValue = Number.isFinite(marketValue) && marketValue !== 0;
  const last = qty > 0 && hasMarketValue ? Math.abs(marketValue) / qty : 0;
  const pl =
    qty > 0 && entry > 0 && last > 0
      ? side === "short"
        ? (entry - last) * qty
        : (last - entry) * qty
      : 0;
  const plPct =
    entry > 0 && last > 0
      ? side === "short"
        ? ((entry - last) / entry) * 100
        : ((last - entry) / entry) * 100
      : 0;
  return {
    brokerAccountId: w.broker_account_id,
    assetClass: normalizeAssetClass(w.asset_class),
    sym: w.symbol,
    companyName: w.company_name ?? null,
    side,
    qty,
    entry,
    last,
    pl,
    plPct,
    stop: typeof w.stop_price === "string" ? Number(w.stop_price) : 0,
    stopStatus: w.stop_status ?? null,
    target: typeof w.target_price === "string" ? Number(w.target_price) : 0,
    targetStatus: w.target_status ?? null,
    age: "",
  };
}

function _wireToClosedStockTrades(
  w: ClosedStockTradesResponseWire,
): { asOf: string; rows: ClosedStockTrade[] } {
  return {
    asOf: w.asOf,
    rows: w.rows.map(_wireToClosedStockTrade),
  };
}

function _wireToClosedStockTrade(w: ClosedStockTradeWire): ClosedStockTrade {
  return {
    fillId: w.fillId,
    approvalId: w.approvalId,
    brokerAccountId: w.brokerAccountId,
    accountLabel: w.accountLabel,
    symbol: w.symbol,
    companyName: w.companyName ?? null,
    side: w.side,
    qty: Number(w.qty),
    entryTime: w.entryTime,
    exitTime: w.exitTime,
    entryPrice: Number(w.entryPrice),
    exitPrice: Number(w.exitPrice),
    realizedPnl: Number(w.realizedPnl),
    rMultiple: w.rMultiple === null ? null : Number(w.rMultiple),
    exitLegType: w.exitLegType,
    strategyId: w.strategyId,
  };
}

function _wireToPositionReassessmentList(
  w: PositionReassessmentListResponseWire,
): { asOf: string; rows: PositionReassessment[] } {
  return {
    asOf: w.asOf,
    rows: w.rows.map(_wireToPositionReassessment),
  };
}

function _wireToPositionReassessment(
  w: PositionReassessmentWire,
): PositionReassessment {
  return {
    reassessmentId: w.reassessmentId,
    snapshotId: w.snapshotId,
    brokerAccountId: w.brokerAccountId,
    symbol: w.symbol,
    companyName: w.companyName ?? null,
    assetClass: w.assetClass,
    side: w.side,
    recommendation: w.recommendation,
    proposedAction: w.proposedAction,
    severity: w.severity,
    confidence: w.confidence,
    status: w.status,
    fingerprint: w.fingerprint,
    currentSnapshot: w.currentSnapshot,
    changedConditions: w.changedConditions,
    deterministicResult: w.deterministicResult,
    llmDebate: w.llmDebate,
    blockers: w.blockers,
    actionPayload: w.actionPayload,
    statusNote: w.statusNote,
    expiresAt: w.expiresAt,
    createdAt: w.createdAt,
    dismissedAt: w.dismissedAt,
    confirmedAt: w.confirmedAt,
    actionResult: w.actionResult,
  };
}

function _wireToRiskLimit(w: RiskLimitWire): RiskLimit {
  return {
    key: (_WIRE_TO_UI_RISK_KEY[w.key] ?? "per_trade_risk") as RiskLimitKey,
    label: w.label,
    value: Number(w.value),
    unit: w.unit,
    quarterlyCap: w.quarterlyCap,
  };
}

function _wireToRiskSnapshot(w: RiskSnapshotResponse): RiskSnapshot {
  return {
    brokerAccountId: String(w.broker_account_id),
    limitsConfigured: Boolean(w.limits_configured),
    dailyLossUsedPct: nullableNumber(w.daily_loss_used_pct),
    weeklyLossUsedPct: nullableNumber(w.weekly_loss_used_pct),
    openPositionsUsed: nullableNumber(w.open_positions_used),
    sectorConcentrationUsedPct: nullableNumber(
      w.sector_concentration_used_pct,
    ),
    maxTradesPerDayUsed: nullableNumber(w.max_trades_per_day_used),
    topSector: typeof w.top_sector === "string" ? w.top_sector : null,
  };
}

function nullableNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function _wireToPositionTrade(w: PositionTradeWire): PositionTrade {
  return {
    id: w.position_trade_id,
    sym: w.symbol,
    strategy: w.strategy_id,
    side: "long",
    qty: 0,
    entry: 0,
    last: 0,
    pl: 0,
    plPct: 0,
    stop: Number(w.current_stop_price),
    stopPct: 0,
    stopRisk: 0,
    stopRiskPct: 0,
    heldDays: w.held_days,
    reviewedDaysAgo: w.reviewed_days_ago,
    reviewOverdueDays: w.review_overdue_days ?? undefined,
    thesis: w.entry_thesis,
    invalidates: w.invalidation_conditions,
    expectedHold: `${w.expected_holding_months} month${w.expected_holding_months === 1 ? "" : "s"}`,
    costBasis: 0,
    currentValue: 0,
  };
}

function _wireToInvestment(w: InvestmentPositionWire): Investment {
  return {
    id: w.investment_position_id,
    sym: w.symbol,
    allocTarget: Number(w.target_allocation_pct) * 100,
    allocCurrent: 0,
    drift: 0,
    costBasis: 0,
    currentValue: 0,
    unrealized: 0,
    unrealizedPct: 0,
    heldMonths: w.held_months,
    longTerm: w.held_months >= 12,
    reviewStatus: w.review_status,
    reviewDueLabel: w.next_review_due_at.slice(0, 10),
    thesis: "",
    sellConditions: [],
    thesisDate: "",
    annualRewriteDue: "",
    taxIfSold: 0,
    researchReportId: w.research_report_id,
    qReviewsCount: 0,
  };
}

function _wireToBasket(w: BasketWire): Basket {
  return {
    id: w.basket_id,
    name: w.name,
    description: w.description,
    members: w.members,
    cap: Number(w.max_concentration_pct) * 100,
    exposure: w.exposure,
    signals24h: w.signals24h,
    forAccount: (w.for_account as unknown) as Basket["forAccount"],
  };
}

function _wireToPendingConfigChange(
  w: PendingConfigChangeWire,
): PendingConfigChange {
  return {
    id: w.id,
    key: (_WIRE_TO_UI_RISK_KEY[w.key] ?? "per_trade_risk") as RiskLimitKey,
    label: w.label,
    currentValue: Number(w.currentValue || "0"),
    newValue: Number(w.newValue),
    unit: w.unit,
    submittedAtDisplay: w.submittedAtDisplay,
    activatesAtDisplay: w.activatesAtDisplay,
    activatesAtMs: w.activatesAtMs,
    reflection: w.reflection,
  };
}

// Phase 5 (d) — Subsystem name → human label map.
const _SUBSYSTEM_LABELS: Record<string, string> = {
  db: "Database",
  scheduler: "Scheduler",
  bus: "Event bus",
  data_feed: "Data feed",
  broker: "Broker",
  ai: "AI",
  reconciliation: "Reconciliation",
  overall: "Overall",
};

function _healthToSystemRows(health: HealthResponse): SystemHealthRow[] {
  // If there are wired subsystems, map them. Otherwise synthesise one
  // "Overall" row from the aggregate status.
  const rows: SystemHealthRow[] =
    health.subsystems.length > 0
      ? health.subsystems.map((s) => ({
          key: s.name as SystemHealthRow["key"],
          label: _SUBSYSTEM_LABELS[s.name] ?? s.name,
          state:
            s.status === "ok"
              ? "ok"
              : s.status === "degraded"
                ? "warn"
                : ("bad" as SystemHealthRow["state"]),
          lastSeen: s.detail || "",
        }))
      : [
          {
            key: "overall" as const,
            label: "Overall",
            state:
              health.status === "ok"
                ? "ok"
                : health.status === "degraded"
                  ? "warn"
                  : ("bad" as SystemHealthRow["state"]),
            lastSeen: "",
          },
        ];
  return rows;
}

function _wireToEventRow(w: EventRowWire): EventRow {
  return {
    eventId: w.event_id ?? null,
    occurredAt: w.occurred_at ?? null,
    time: w.time,
    type: w.type,
    summary: w.summary,
    actor: w.actor,
  };
}

function _wireToChartSnapshot(w: ChartSnapshotWire): ChartSnapshot {
  return {
    symbol: w.symbol,
    timeframe: w.timeframe,
    bars: w.bars.map((b) => ({
      time: b.time,
      open: Number(b.open),
      high: Number(b.high),
      low: Number(b.low),
      close: Number(b.close),
      volume: b.volume,
    })),
    overlays: {
      sma20: w.overlays.sma20.map(_wireToLinePoint),
      sma50: w.overlays.sma50.map(_wireToLinePoint),
      sma200: w.overlays.sma200.map(_wireToLinePoint),
      rsi14: w.overlays.rsi14.map(_wireToLinePoint),
    },
    levels: w.levels.map((l) => ({
      id: l.id,
      kind: l.kind,
      price: Number(l.price),
      label: l.label,
      status: l.status,
    })),
    markers: w.markers.map((m) => ({
      id: m.id,
      kind: m.kind,
      time: m.time,
      price: Number(m.price),
      text: m.text,
      position: m.position,
      color: m.color,
      shape: m.shape,
    })),
    trades: w.trades.map((t) => ({
      approvalId: t.approvalId,
      entryTime: t.entryTime,
      exitTime: t.exitTime,
      entryPrice: Number(t.entryPrice),
      exitPrice: Number(t.exitPrice),
      qty: Number(t.qty),
      pnl: Number(t.pnl),
      rMultiple: t.rMultiple === null ? null : Number(t.rMultiple),
    })),
  };
}

function _wireToLinePoint(
  p: ChartSnapshotWire["overlays"]["sma20"][number],
) {
  return { time: p.time, value: Number(p.value) };
}

function _wireToGeneratedSignal(w: SignalWire): GeneratedSignal {
  return {
    signalId: w.signal_id ?? null,
    symbol: w.symbol,
    companyName: w.company_name ?? null,
    strategyId: w.strategy_id,
    assetClass: w.asset_class ?? "equity",
    quantityStep: w.quantity_step ?? null,
    minQuantity: w.min_quantity ?? null,
    minNotional: w.min_notional ?? null,
    barDate: w.bar_date,
    direction: w.direction,
    entry: Number(w.entry_price),
    stop: Number(w.stop_price),
    targets: w.target_prices.map((p) => Number(p)),
    score: w.score,
    factors: w.factors,
    rrRatio: Number(w.rr_ratio),
    aiConfidence: w.ai_confidence,
    aiRationale: w.ai_rationale,
    aiRiskFlags: w.ai_risk_flags,
    aiModelId: w.ai_model_id,
    researchEnrichment: _wireToResearchEnrichment(w.research_enrichment),
    edgeScore: w.edge_score ?? null,
    regimeFit: w.regime_fit ?? null,
    portfolioFit: w.portfolio_fit ?? null,
    approvalGateReason: w.approval_gate_reason ?? null,
    signalRole: w.signal_role ?? "entry",
  };
}

function _wireToSignalFailure(w: SignalFailureWire): SignalFailure {
  return {
    symbol: w.symbol,
    errorType: w.error_type,
    detail: w.detail,
  };
}

function _wireToSignalHistory(w: SignalHistoryWire): SignalHistoryRow {
  return {
    signalId: w.signal_id,
    generatedAt: w.generated_at,
    symbol: w.symbol,
    companyName: w.company_name ?? null,
    strategyId: w.strategy_id,
    assetClass: w.asset_class ?? "equity",
    barDate: w.bar_date,
    direction: w.direction,
    timeframe: w.timeframe,
    entry: Number(w.entry_price),
    stop: Number(w.stop_price),
    targets: w.target_prices.map((price) => Number(price)),
    rrRatio: Number(w.rr_ratio),
    score: w.score,
    rationale: w.rationale,
    aiModelId: w.ai_model_id,
    aiInputMode: w.ai_input_mode,
    researchEnrichment: _wireToResearchEnrichment(w.research_enrichment),
    edgeScore: w.edge_score ?? null,
    regimeFit: w.regime_fit ?? null,
    portfolioFit: w.portfolio_fit ?? null,
    approvalGateReason: w.approval_gate_reason ?? null,
    signalRole: w.signal_role ?? "entry",
    approvalStatus: w.approval_status,
    approvalId: w.approval_id,
    approvalRequestedAt: w.approval_requested_at,
    approvalExpiresAt: w.approval_expires_at,
    approvalConsumedAt: w.approval_consumed_at,
  };
}

function _wireToEvaluationResult(w: EvaluationResultWire): EvaluationResult {
  return {
    evaluationId: w.evaluation_id,
    symbol: w.symbol,
    isEtf: w.is_etf,
    horizon: w.horizon as EvaluationResult["horizon"],
    asOfDate: w.as_of_date,
    investmentStance: w.investment_stance,
    tradeStance: w.trade_stance,
    compositeScore: w.composite_score,
    confidence: w.confidence,
    regime: w.regime,
    strategyVotes: w.strategy_votes.map((v) => ({
      strategyId: v.strategy_id,
      vote: v.vote,
      score: v.score,
      factors: v.factors,
      direction: v.direction,
      entryPrice: v.entry_price !== null ? Number(v.entry_price) : null,
      stopPrice: v.stop_price !== null ? Number(v.stop_price) : null,
      targetPrices: v.target_prices.map(Number),
      reason: v.reason,
    })),
    entryPrice: w.entry_price !== null ? Number(w.entry_price) : null,
    stopPrice: w.stop_price !== null ? Number(w.stop_price) : null,
    targetPrices: w.target_prices.map(Number),
    invalidationText: w.invalidation_text,
    newsCount: w.news_count,
    hasFundamentals: w.has_fundamentals,
    hasMacro: w.has_macro,
    portfolioAlreadyHeld: w.portfolio_already_held,
    livePrice: w.live_price !== undefined && w.live_price !== null
      ? Number(w.live_price)
      : null,
    livePriceSource: w.live_price_source ?? "unavailable",
    latestBarDate: w.latest_bar_date ?? null,
    latestBarClose: w.latest_bar_close !== undefined && w.latest_bar_close !== null
      ? Number(w.latest_bar_close)
      : null,
    latestNewsAt: w.latest_news_at ?? null,
    scoreDrivers: w.score_drivers ?? [],
    dataFreshness: w.data_freshness ?? {},
    analystSummary: w.analyst_summary ?? "",
    bullCase: w.bull_case ?? [],
    bearCase: w.bear_case ?? [],
    keyRisks: w.key_risks ?? [],
    fundamentalsSummary: w.fundamentals_summary ?? [],
    macroSummary: w.macro_summary ?? [],
    newsDigest: w.news_digest ?? [],
    valuationSummary: w.valuation_summary ?? [],
    sectorPeerSummary: w.sector_peer_summary ?? [],
    newsCatalysts: w.news_catalysts ?? [],
    sourceNotes: w.source_notes ?? [],
    scoreAttribution: w.score_attribution ?? {},
    peerContext: w.peer_context ?? {},
    sectorContext: w.sector_context ?? {},
    filingContext: w.filing_context ?? {},
    aiMemoStatus: w.ai_memo_status ?? "unavailable",
    aiMemoModel: w.ai_memo_model ?? null,
    aiMemoCost: w.ai_memo_cost ?? null,
    aiMemoError: w.ai_memo_error ?? null,
    tradePlan: w.trade_plan ?? {},
    chartLevels: w.chart_levels.map((l) => ({
      id: l.id,
      kind: l.kind,
      price: Number(l.price),
      label: l.label,
      status: l.status,
    })),
    chartMarkers: w.chart_markers.map((m) => ({
      id: m.id,
      kind: m.kind,
      time: m.time,
      price: Number(m.price),
      text: m.text,
      position: m.position,
      color: m.color,
      shape: m.shape,
    })),
    socialSentiment: w.social_sentiment
      ? _wireToSocialSentimentSnapshot(w.social_sentiment)
      : null,
    agentReview: w.agent_review
      ? _wireToMultiAgentReview(w.agent_review)
      : null,
    evaluatedAt: w.evaluated_at,
  };
}

function _wireToSocialSentimentSnapshot(
  w: SocialSentimentSnapshotWire,
): SocialSentimentSnapshot {
  return {
    snapshotId: w.snapshot_id,
    symbol: w.symbol,
    asOf: w.as_of,
    providerStatus: w.provider_status ?? {},
    filteredCount: w.filtered_count ?? 0,
    postCount: w.post_count ?? 0,
    counts: w.counts ?? {},
    posts: (w.posts ?? []).map((post) => ({
      postId: post.post_id,
      source: post.source,
      author: post.author,
      publishedAt: post.published_at,
      sentiment: post.sentiment,
      engagement: post.engagement ?? {},
      body: post.body,
      url: post.url,
    })),
  };
}

function _wireToMultiAgentReview(
  w: MultiAgentReviewResultWire,
): MultiAgentReviewResult {
  return {
    reviewId: w.review_id,
    evaluationId: w.evaluation_id,
    workflowRunId: w.workflow_run_id,
    symbol: w.symbol,
    asOfDate: w.as_of_date,
    status: w.status,
    synthesisSummary: w.synthesis_summary,
    keyOpportunities: w.key_opportunities ?? [],
    keyRisks: w.key_risks ?? [],
    citations: w.citations ?? [],
    agentSteps: (w.agent_steps ?? []).map((step) => ({
      role: step.role,
      stance: step.stance,
      summary: step.summary,
      keyPoints: step.key_points ?? [],
      concerns: step.concerns ?? [],
      sourceIds: step.source_ids ?? [],
    })),
    sourceCompositeScore: w.source_composite_score,
    promptVersion: w.prompt_version,
    modelId: w.model_id,
    createdAt: w.created_at,
    metadata: w.metadata ?? {},
  };
}

function _wireToRunAgentReviewResult(
  w: RunAgentReviewResponseWire,
): RunAgentReviewResult {
  return {
    review: _wireToMultiAgentReview(w.review),
    workflow: w.workflow,
    socialSentiment: w.social_sentiment
      ? _wireToSocialSentimentSnapshot(w.social_sentiment)
      : null,
  };
}

function _wireToShadowAgentReviewRunResult(
  w: ShadowAgentReviewRunResponseWire,
): ShadowAgentReviewRunResult {
  return {
    workflow: w.workflow,
    reviewed: w.reviewed,
    failed: w.failed,
    reviewIds: w.review_ids ?? [],
    failures: w.failures ?? [],
  };
}

function _wireToWorkflowStatus(
  w: AIWorkflowStatusResponseWire,
): AIWorkflowStatus {
  return {
    run: w.run ?? {},
    steps: w.steps ?? [],
  };
}

function _wireToDecisionReflection(
  w: DecisionReflectionWire,
): DecisionReflection {
  return {
    reflectionId: w.reflection_id,
    sourceDecisionType: w.source_decision_type,
    sourceDecisionId: w.source_decision_id,
    symbol: w.symbol,
    decisionAt: w.decision_at,
    evaluatedAt: w.evaluated_at,
    holdingWindowDays: w.holding_window_days,
    rawReturnPct: w.raw_return_pct,
    benchmarkReturnPct: w.benchmark_return_pct,
    relativeReturnPct: w.relative_return_pct,
    lesson: w.lesson,
    confidence: w.confidence,
    promptVersion: w.prompt_version,
    modelId: w.model_id,
    metadata: w.metadata ?? {},
  };
}

function _wireToSavedResearchEvaluation(
  w: SavedResearchEvaluationWire,
): SavedResearchEvaluation {
  return {
    ..._wireToEvaluationResult(w),
    acceptedAt: w.accepted_at,
    approvalId: w.approval_id,
  };
}

function _wireToResearchReport(w: ResearchReportWire): ResearchReportRow {
  return {
    id: w.research_report_id,
    symbol: w.symbol,
    requestedBy: w.requested_by,
    requestedAt: w.requested_at,
    completedAt: w.completed_at,
    modelVersion: w.model_version,
    promptVersion: w.prompt_version,
    assetClass: w.asset_class,
    primaryDocuments: w.primary_documents ?? [],
    newsHeadlinesUsed: w.news_headlines_used,
    fundamentalsUsed: w.fundamentals_used,
    reportKind: w.report_kind ?? "research_v1",
    sectionLabels: w.section_labels ?? {},
    suggestedSignal: w.suggested_signal
      ? _wireToResearchSuggestedSignal(w.suggested_signal)
      : null,
    reportText: w.report_text,
    reportSections: w.report_sections ?? {},
    bullCaseLength: w.bull_case_length,
    bearCaseLength: w.bear_case_length,
    citationCount: w.citation_count,
    primaryDocQuoteCount: w.primary_doc_quote_count,
    validatorPassed: w.validator_passed,
    validatorFailures: w.validator_failures ?? [],
    cost: Number(w.cost_dollars),
    cachedUntil: w.cached_until,
    cachedExpired: w.cached_expired,
    supersededBy: w.superseded_by,
  };
}

function _wireToResearchReportCreateResult(
  w: ResearchReportCreateResponseWire,
): ResearchReportCreateResult {
  return {
    id: w.research_report_id,
    symbol: w.symbol,
    reportKind: w.report_kind ?? "research_v1",
    validatorPassed: w.validator_passed,
    cacheHit: w.cache_hit,
    cost: Number(w.cost_dollars),
    completedAt: w.completed_at,
    cachedUntil: w.cached_until,
    suggestedSignal: w.suggested_signal
      ? _wireToResearchSuggestedSignal(w.suggested_signal)
      : null,
    validatorFailures: w.validator_failures ?? [],
  };
}

function _wireToResearchSuggestedSignal(
  w: ResearchSuggestedSignalWire,
): ResearchSuggestedSignal {
  return {
    mode: w.mode,
    stance: w.stance,
    entryPrice: w.entry_price,
    stopPrice: w.stop_price,
    targetPrices: w.target_prices ?? [],
    riskRewardRatio: w.risk_reward_ratio,
    strategyId: w.strategy_id,
    confidence: w.confidence,
    invalidationText: w.invalidation_text,
    thesisSummary: w.thesis_summary,
    llmAdjustmentNote: w.llm_adjustment_note,
    validationStatus: w.validation_status,
    source: w.source,
    evaluatedAt: w.evaluated_at,
    expiresAt: w.expires_at,
  };
}

function _wireToLivePnL(w: LivePnLResponseWire): LivePnLSnapshot {
  return {
    asOf: w.as_of,
    marketOpen: w.market_open,
    entries: w.entries.map(
      (entry): LivePnLEntry => ({
        brokerAccountId: entry.broker_account_id,
        symbol: entry.symbol,
        qty: Number(entry.qty),
        avgEntryPrice: Number(entry.avg_entry_price),
        currentPrice:
          entry.current_price !== null ? Number(entry.current_price) : null,
        unrealizedPnL: Number(entry.unrealized_pnl),
        unrealizedPnLPct: Number(entry.unrealized_pnl_pct),
        stale: entry.stale,
      }),
    ),
  };
}

// ---------------------------------------------------------------------
// Factory — picks the implementation per env
// ---------------------------------------------------------------------

/**
 * Async because the fixture path uses dynamic import to keep the
 * fixture module out of production bundles. Production builds
 * (VITE_USE_FIXTURES unset / "false") skip the import entirely
 * and resolve synchronously to ApiDataSource.
 */
export async function createDataSource(): Promise<DataSource> {
  if (useFixtures()) {
    const mod = await import("./fixtureDataSource");
    return new mod.FixtureDataSource();
  }
  return new ApiDataSource(new ApiClient());
}
